import { MapPinIcon } from 'lucide-preact';

import { Button } from '@/components/Buttons';
import { cn } from '@/lib/utils';

export interface PausedBannerButton {
  label: string;
  onClick?: () => void;
}

interface PausedBannerProps {
  currentSection?: string;
  nextHabitat?: string;
  mascotSrc: string;
  /** Rendered in order; the last one is the trailing action. */
  buttons?: PausedBannerButton[];
  className?: string;
}

const PausedBanner = ({ currentSection, nextHabitat, mascotSrc, buttons = [], className }: PausedBannerProps) => (
  <div className={cn('preflight relative h-[85px] shrink-0 grow-0 overflow-hidden bg-forest-core', className)}>
    <img src={mascotSrc} alt="Zoolife mascot" className="preflight preflight-img absolute bottom-0 left-3 w-[84px]" />

    <div className="preflight flex h-full">
      <div className="preflight w-[126px] shrink-0" />

      <div className="preflight relative min-w-0 flex-1">
        <p className="preflight preflight-p absolute top-[8px] text-[9px] font-bold uppercase leading-none text-tea-200">
          TOUR-GUIDE
        </p>

        <div className="preflight absolute top-[20px] flex items-center gap-[3px]">
          <p className="preflight preflight-p text-[18px] font-bold leading-none text-white">
            {currentSection ?? 'Departure'}
          </p>
          <MapPinIcon className="preflight h-[17px] w-[17px] text-white" />
        </div>

        <p className="preflight preflight-p absolute left-[289px] top-[8px] text-[9px] font-bold uppercase leading-none text-tea-200">
          NEXT HABITAT
        </p>

        <p className="preflight preflight-p absolute left-[289px] top-[20px] text-[18px] font-bold leading-none text-white">
          {nextHabitat ?? '\u2014'}
        </p>

        <div className="preflight absolute top-[45px] flex h-[28px] items-center">
          <div className="preflight z-10 h-[24px] w-[24px] shrink-0 rounded-full border-[3px] border-sun-400 bg-white" />
          <div className="preflight -mx-1 h-[8px] w-[266px] shrink-0 bg-sun-400" />
          <div className="preflight z-10 h-[24px] w-[24px] shrink-0 rounded-full bg-sun-400" />
          <div className="preflight -mx-1 h-[8px] w-[225px] shrink-0 bg-gradient-to-r from-sun-400 to-transparent" />
        </div>
      </div>

      <div className="preflight flex shrink-0 items-center gap-[7px] pr-[14px]">
        {buttons.map((btn, i) => {
          const isLast = i === buttons.length - 1;

          return (
            <Button
              key={btn.label}
              variant={isLast ? 'tertiary' : 'outline'}
              onClick={btn.onClick}
              className={cn(
                'h-[42px] whitespace-nowrap px-[20px] text-[16px] font-bold',
                // The trailing action sizes to its label; the ones before it keep the fixed width the
                // layout was measured against.
                !isLast && 'w-[244px]',
                // `outline` is forest-core on transparent — the banner's own colour, so it would
                // disappear here. Same shape, inverted for a dark background.
                !isLast && 'border-white text-white hover:bg-white/10',
              )}
            >
              {btn.label}
            </Button>
          );
        })}
      </div>
    </div>
  </div>
);

export default PausedBanner;
